import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import WebFont from 'webfontloader'
import {client} from 'lib/ui/api-client'
import {useAsync} from 'lib/async'

const TRENDING_FONT_FAMILIES = [
  'Arial',
  'Courier New',
  'Montserrat',
  'Open Sans',
  'Oswald',
  'Pacifico',
  'Permanent Marker',
  'Poppins',
  'Roboto',
  'Shadows Into Light',
  'Times New Roman',
  'Ubuntu',
  'Verdana',
  'Water Brush',
]

type GoogleFont = {
  family: string
  variants: string[]
}

type GoogleFontsResponse = {
  kind: string
  items: GoogleFont[]
}

function useGoogleFonts() {
  const request = useCallback(() => {
    return client.get<GoogleFontsResponse>(
      `https://www.googleapis.com/webfonts/v1/webfonts?fields=items(family%2Cvariants)&sort=TRENDING&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
    )
  }, [])

  return useAsync(request)
}

function loadFont(family: string) {
  if (TRENDING_FONT_FAMILIES.includes(family)) return
  try {
    WebFont.load({google: {families: [family]}})
  } catch {
    // ignore load errors
  }
}

type FontFamilyPickerProps = {
  value: string
  onChange: (fontFamily: string) => void
}

export default function FontFamilyPicker({
  value,
  onChange,
}: FontFamilyPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const {data} = useGoogleFonts()

  const allFonts = useMemo(() => {
    const googleFonts = data?.items?.map((f) => f.family) ?? []
    const googleSet = new Set(googleFonts)
    // Trending first, then Google fonts that aren't trending
    const rest = googleFonts.filter((f) => !TRENDING_FONT_FAMILIES.includes(f))
    // Add any trending fonts not in Google list
    const trending = TRENDING_FONT_FAMILIES.filter(
      (f) => !googleSet.has(f) || googleSet.has(f),
    )
    return [...trending, ...rest]
  }, [data])

  const filtered = useMemo(() => {
    if (!search) return allFonts.slice(0, 30)
    const lower = search.toLowerCase()
    return allFonts.filter((f) => f.toLowerCase().includes(lower)).slice(0, 30)
  }, [search, allFonts])

  // Reset active index when filtered list changes
  useEffect(() => {
    setActiveIndex(-1)
  }, [filtered])

  // Display label: extract clean family name from value like "Arial, sans-serif"
  const displayLabel = useMemo(() => {
    if (!value) return 'Default Font'
    // Strip fallback families
    const clean = value.replace(/['",]/g, '').split(/\s*,\s*/)[0]
    return clean || 'Default Font'
  }, [value])

  const handleSelect = useCallback(
    (family: string) => {
      loadFont(family)
      onChange(family)
      setOpen(false)
      setSearch('')
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    onChange('')
    setOpen(false)
    setSearch('')
  }, [onChange])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Focus input when opening
  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return
    const items = listRef.current.children
    if (items[activeIndex]) {
      ;(items[activeIndex] as HTMLElement).scrollIntoView({block: 'nearest'})
    }
  }, [activeIndex])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (activeIndex >= 0 && filtered[activeIndex]) {
          handleSelect(filtered[activeIndex])
        }
      } else if (e.key === 'Escape') {
        setOpen(false)
        setSearch('')
      }
    },
    [filtered, activeIndex, handleSelect],
  )

  const trendingSet = useMemo(() => new Set(TRENDING_FONT_FAMILIES), [])

  return (
    <div className="html-editor-font-picker" ref={containerRef}>
      <button
        type="button"
        className="html-editor-font-picker__trigger"
        onClick={() => setOpen((o) => !o)}
        style={{fontFamily: value || 'inherit'}}
      >
        <span className="html-editor-font-picker__label">{displayLabel}</span>
        <span className="html-editor-font-picker__arrow">&#9662;</span>
      </button>

      {open && (
        <div className="html-editor-font-picker__dropdown">
          <input
            ref={inputRef}
            className="html-editor-font-picker__search"
            type="text"
            placeholder="Search fonts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="html-editor-font-picker__list" ref={listRef}>
            {!search && (
              <button
                type="button"
                className={`html-editor-font-picker__option ${
                  !value ? 'html-editor-font-picker__option--selected' : ''
                }`}
                onClick={handleClear}
              >
                Default Font
              </button>
            )}
            {filtered.map((family, i) => (
              <FontOption
                key={family}
                family={family}
                active={i === activeIndex}
                selected={displayLabel === family}
                isTrending={trendingSet.has(family)}
                onSelect={handleSelect}
              />
            ))}
            {filtered.length === 0 && (
              <div className="html-editor-font-picker__empty">
                No fonts found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function FontOption({
  family,
  active,
  selected,
  isTrending,
  onSelect,
}: {
  family: string
  active: boolean
  selected: boolean
  isTrending: boolean
  onSelect: (family: string) => void
}) {
  const loaded = useRef(false)
  const ref = useRef<HTMLButtonElement>(null)

  // Load non-trending fonts when they appear in the list
  useEffect(() => {
    if (!isTrending && !loaded.current) {
      loaded.current = true
      loadFont(family)
    }
  }, [family, isTrending])

  const classNames = [
    'html-editor-font-picker__option',
    active ? 'html-editor-font-picker__option--active' : '',
    selected ? 'html-editor-font-picker__option--selected' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={ref}
      type="button"
      className={classNames}
      style={{fontFamily: `${family}, sans-serif`}}
      onMouseDown={(e) => {
        e.preventDefault()
        onSelect(family)
      }}
    >
      {family}
    </button>
  )
}
